import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { css } from '@emotion/react';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import Typography from '@mui/material/Typography';
import color from 'color';
import { useCallback, useMemo } from 'react';
import { toggleExpandableSection } from '../redux/actions/sessionActions';
import { useAppDispatch, useAppSelector } from '../redux/store';
import { DRAWER_EXPANDABLE_SECTION_CONTENT_COLOR, DRAWER_EXPANDABLE_SECTION_HEADER_COLOR, DRAWER_EXPANDABLE_SECTION_SHADOW_COLOR, FRIENDS_PANEL_SECTION_CONTENT_COLOR, FRIENDS_PANEL_SECTION_HEADER_COLOR, FRIENDS_PANEL_SECTION_SHADOW_COLOR, PLAYER_INFO_PANEL_SECTION_CONTENT_COLOR, PLAYER_INFO_PANEL_SECTION_HEADER_COLOR, PLAYER_INFO_PANEL_SECTION_SHADOW_COLOR } from '../styles/colors';
import { ALL_EXPANDABLE_SECTION_PROPERTIES } from '../types/expandableSectionTypes';
import { IconWithLabelDisplay } from './iconWithLabelDisplay';
const BORDER_WIDTH_PX = 2;
const ALL_COLOR_TYPE_PROPERTIES = {
    'player-info': createColorTypeProperties({
        headerColor: PLAYER_INFO_PANEL_SECTION_HEADER_COLOR,
        contentColor: PLAYER_INFO_PANEL_SECTION_CONTENT_COLOR,
        shadowColor: PLAYER_INFO_PANEL_SECTION_SHADOW_COLOR
    }),
    friends: createColorTypeProperties({
        headerColor: FRIENDS_PANEL_SECTION_HEADER_COLOR,
        contentColor: FRIENDS_PANEL_SECTION_CONTENT_COLOR,
        shadowColor: FRIENDS_PANEL_SECTION_SHADOW_COLOR
    }),
    drawer: createColorTypeProperties({
        headerColor: DRAWER_EXPANDABLE_SECTION_HEADER_COLOR,
        contentColor: DRAWER_EXPANDABLE_SECTION_CONTENT_COLOR,
        shadowColor: DRAWER_EXPANDABLE_SECTION_SHADOW_COLOR,
        expandOrCollapseIconOpacity: 0.3
    })
};
const HEADER_TITLE_CSS = css `
  flex-grow: 1;
`;
function createColorTypeProperties({ headerColor, contentColor, shadowColor, expandOrCollapseIconOpacity }) {
    const baseHeaderCss = css `
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    background-color: ${headerColor};
  `;
    const containerCss = css `
    margin: 10px 5px 0 5px;
    display: flex;
    flex-direction: column;
    border-radius: 10px;
    filter: drop-shadow(3px 3px 0 ${shadowColor});
  `;
    const baseExpandedHeaderCss = css(baseHeaderCss, css `
      padding: 10px;
      border-top-left-radius: 8px;
      border-top-right-radius: 8px;
    `);
    const headerHoverCss = css `
    cursor: pointer;
    user-select: none;
    &:hover {
      background-color: ${color(headerColor).lighten(0.1).hex()};
    }
  `;
    const activeExpandedHeaderCss = css(baseExpandedHeaderCss, headerHoverCss, css `
      padding: 10px;
      border-left: ${BORDER_WIDTH_PX}px solid ${headerColor};
      border-top: ${BORDER_WIDTH_PX}px solid ${headerColor};
      border-right: ${BORDER_WIDTH_PX}px solid ${headerColor};
    `);
    const inactiveExpandedHeaderCss = baseExpandedHeaderCss;
    const collapsedHeaderCss = css(baseHeaderCss, headerHoverCss, css `
      padding: 10px;
      border: ${BORDER_WIDTH_PX}px solid ${headerColor};
      border-radius: 8px;
    `);
    const bodyCss = css `
    padding: 5px;
    background-color: ${contentColor};
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
    border-left: ${BORDER_WIDTH_PX}px solid ${headerColor};
    border-bottom: ${BORDER_WIDTH_PX}px solid ${headerColor};
    border-right: ${BORDER_WIDTH_PX}px solid ${headerColor};
    display: flex;
    flex-direction: column;
  `;
    return {
        activeExpandedHeaderCss,
        bodyCss,
        collapsedHeaderCss,
        containerCss,
        inactiveExpandedHeaderCss,
        expandOrCollapseIconOpacity
    };
}
function renderHeaderTitle(title, expandableSectionType, overrideExpandableSectionTypeTitle) {
    if (title) {
        return title;
    }
    if (!expandableSectionType) {
        return null;
    }
    const { svgIconComponent: SvgIconComponent, label, color } = ALL_EXPANDABLE_SECTION_PROPERTIES[expandableSectionType];
    const labelOrOverridenLabel = overrideExpandableSectionTypeTitle || label;
    if (!SvgIconComponent) {
        return labelOrOverridenLabel;
    }
    return (_jsx(IconWithLabelDisplay, { icon: _jsx(SvgIconComponent, { style: color !== undefined ? { color } : undefined }), label: labelOrOverridenLabel }));
}
function renderExpandCollapseButton(isExpanded, expandOrCollapseIconOpacity) {
    const style = expandOrCollapseIconOpacity !== undefined ? { opacity: expandOrCollapseIconOpacity } : undefined;
    if (isExpanded === true) {
        return (_jsx("span", { title: 'Collapse', style: style, children: _jsx(ExpandLessRoundedIcon, {}) }));
    }
    if (isExpanded === false) {
        return (_jsx("span", { title: 'Expand', style: style, children: _jsx(ExpandMoreRoundedIcon, {}) }));
    }
}
export const ExpandableSection = ({ children, colorType, overrideExpandableSectionTypeTitle, title, headerRight, expandableSectionType }) => {
    const dispatch = useAppDispatch();
    const isExpanded = useAppSelector((state) => expandableSectionType ? !!state.session.expandableSectionStates[expandableSectionType] : undefined);
    const { activeExpandedHeaderCss, bodyCss, collapsedHeaderCss, containerCss, inactiveExpandedHeaderCss, expandOrCollapseIconOpacity } = ALL_COLOR_TYPE_PROPERTIES[colorType];
    const onExpandCollapse = useCallback(() => {
        if (isExpanded === undefined || !expandableSectionType) {
            return;
        }
        dispatch(toggleExpandableSection(expandableSectionType));
    }, [dispatch, expandableSectionType, isExpanded]);
    const expandCollapseButton = renderExpandCollapseButton(isExpanded, expandOrCollapseIconOpacity);
    const headerTitleCss = useMemo(() => {
        const expandableSectionColor = expandableSectionType && ALL_EXPANDABLE_SECTION_PROPERTIES[expandableSectionType].color;
        return expandableSectionColor !== undefined
            ? css(HEADER_TITLE_CSS, css `
            color: ${expandableSectionColor};
          `)
            : HEADER_TITLE_CSS;
    }, [expandableSectionType]);
    const headerContents = (_jsxs(_Fragment, { children: [_jsx(Typography, { component: 'div', css: headerTitleCss, "aria-hidden": true, children: renderHeaderTitle(title, expandableSectionType, overrideExpandableSectionTypeTitle) }), headerRight && _jsx("div", { children: headerRight }), expandCollapseButton && _jsx("div", { children: expandCollapseButton })] }));
    const ariaLabel = title || (expandableSectionType && ALL_EXPANDABLE_SECTION_PROPERTIES[expandableSectionType].label);
    const borderColorCss = useMemo(() => {
        const expandableSectionColor = expandableSectionType && ALL_EXPANDABLE_SECTION_PROPERTIES[expandableSectionType].color;
        if (expandableSectionColor !== undefined) {
            return css `
        border-color: ${expandableSectionColor};
      `;
        }
    }, [expandableSectionType]);
    const collapsedHeaderTotalCss = useMemo(() => css(collapsedHeaderCss, borderColorCss), [borderColorCss, collapsedHeaderCss]);
    const expandedHeaderTotalCss = useMemo(() => css(isExpanded === true ? activeExpandedHeaderCss : inactiveExpandedHeaderCss, borderColorCss), [activeExpandedHeaderCss, borderColorCss, inactiveExpandedHeaderCss, isExpanded]);
    const expandedBodyTotalCss = useMemo(() => css(bodyCss, borderColorCss), [bodyCss, borderColorCss]);
    if (isExpanded === false) {
        return (_jsx("section", { css: containerCss, "aria-label": ariaLabel, onClick: onExpandCollapse, children: _jsx("div", { css: collapsedHeaderTotalCss, children: headerContents }) }));
    }
    return (_jsxs("section", { css: containerCss, "aria-label": ariaLabel, children: [_jsx("div", { css: expandedHeaderTotalCss, onClick: isExpanded === true ? onExpandCollapse : undefined, children: headerContents }), _jsx("div", { css: expandedBodyTotalCss, children: children })] }));
};
